import React, { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import ReCAPTCHA from "react-google-recaptcha";
import Notification from "../components/Notification";
import {
  getRegistrationFieldLabel,
  getRegistrationFormDataKey,
  SHORT_TEXT_REGISTRATION_FIELD_IDS,
} from "../utils/registrationFields";
import {
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  ExclamationTriangleIcon,
  HandRaisedIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  CreditCardIcon,
} from "@heroicons/react/24/outline";

interface EventRegistrationField {
  id: string;
  type: string;
  label: string;
  required: boolean;
  included: boolean;
  order: number;
}

interface Event {
  id: string;
  event_name: string;
  from_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  type: string;
  charge_for_event?: boolean;
  charge_amount?: number;
  registration_title?: string;
  registration_body?: string;
  registration_image?: string;
  registration_fields?: string[];
  custom_terms_conditions?: string;
  studio_id?: string;
  show_studio_address_on_registration?: boolean;
  studio_address?: string | null;
  studio?: {
    name: string;
  };
}

interface RegistrationData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  dateOfBirth?: string;
  address?: string;
  emergencyContact?: string;
  allergies?: string;
  medicalInfo?: string;
  experienceLevel?: string;
  beltRank?: string;
  beltSize?: string;
  uniformSize?: string;
  tShirtSize?: string;
  howDidYouHear?: string;
  parentName?: string;
  parentPhone?: string;
  parentEmail?: string;
  termsConditions?: boolean;
}

const FIELD_ICONS = {
  first_name: UserIcon,
  last_name: UserIcon,
  email: EnvelopeIcon,
  phone: PhoneIcon,
  date_of_birth: CalendarIcon,
  address: MapPinIcon,
  emergency_contact: ExclamationTriangleIcon,
  allergies: ExclamationTriangleIcon,
  medical_info: ExclamationTriangleIcon,
  experience_level: UserIcon,
  belt_rank: UserIcon,
  belt_size: UserIcon,
  uniform_size: UserIcon,
  t_shirt_size: UserIcon,
  how_did_you_hear: UserIcon,
  parent_name: UserIcon,
  parent_phone: PhoneIcon,
  parent_email: EnvelopeIcon,
};

const getInputClassName = (baseClasses: string) => {
  return `${baseClasses} bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600`;
};

const formatDateInput = (value: string): string => {
  const numbers = value.replace(/\D/g, '');
  const limited = numbers.slice(0, 8);
  
  if (limited.length >= 5) {
    return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`;
  } else if (limited.length >= 3) {
    return `${limited.slice(0, 2)}/${limited.slice(2)}`;
  } else if (limited.length >= 1) {
    return limited;
  }
  
  return '';
};

// Security constants
const MAX_LENGTHS: Record<string, number> = {
  firstName: 50,
  lastName: 50,
  email: 254,
  phone: 20,
  address: 500,
  emergencyContact: 100,
  allergies: 200,
  medicalInfo: 500,
  shortText: 50,
  howDidYouHear: 100,
  parentName: 100,
  parentPhone: 20,
  parentEmail: 254,
  dateOfBirth: 10,
};

const SUBMISSION_COOLDOWN = 2000; // 2 seconds between submissions

const HOW_DID_YOU_HEAR_OPTIONS = [
  "Referral",
  "Local Partnerships",
  "Community Events & Booths",
  "Walk-in",
  "Internet Search",
  "Internet Ads",
  "Website Lead Capture",
  "Cross-Promotions",
  "Email Marketing",
  "Social Media",
  "Social Media Advertisements",
  "Other",
];

// Input sanitization function
const sanitizeInput = (input: string, fieldType?: string): string => {
  if (typeof input !== 'string') return '';
  
  let sanitized = input;
  
  // For text fields (names, addresses), preserve spaces and only remove dangerous content
  if (fieldType === 'address' || 
      fieldType === 'firstName' || 
      fieldType === 'lastName' || 
      fieldType === 'emergencyContact' || 
      fieldType === 'parentName' ||
      fieldType === 'allergies' ||
      fieldType === 'medicalInfo' ||
      fieldType === 'shortText') {
    
    // Only remove dangerous patterns, preserve ALL spaces (including trailing)
    sanitized = sanitized.replace(/[<>]/g, ''); // Remove HTML tags
    sanitized = sanitized.replace(/javascript:/gi, ''); // Remove javascript protocol
    sanitized = sanitized.replace(/vbscript:/gi, ''); // Remove vbscript protocol
    sanitized = sanitized.replace(/on\w+\s*=/gi, ''); // Remove event handlers
    sanitized = sanitized.replace(/data:text\/html/gi, ''); // Remove data URLs
    
    // DON'T trim for text fields - preserve all spaces including trailing ones
    // Users should be able to type "John " and keep the space
    
  } else {
    // For other fields, use more aggressive sanitization
    sanitized = sanitized.trim();
    sanitized = sanitized.replace(/[<>]/g, '');
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/data:text\/html/gi, '');
    sanitized = sanitized.replace(/vbscript:/gi, '');
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');
  }
  
  return sanitized;
};

// Enhanced validation function
const validateInput = (value: string, type: string): boolean => {
  if (!value || typeof value !== 'string') return false;
  
  const sanitized = sanitizeInput(value, type);
  if (sanitized.length === 0) return false;

  // Check length limits
  if (MAX_LENGTHS[type] && sanitized.length > MAX_LENGTHS[type]) {
    return false;
  }
  
  // Type-specific validation
  switch (type) {
    case 'email':
    case 'parentEmail':
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailRegex.test(sanitized);
    case 'phone':
    case 'parentPhone':
      const phoneRegex = /^[\d\s\-\(\)\.]+$/;
      return phoneRegex.test(sanitized) && sanitized.replace(/\D/g, '').length >= 10;
    case 'dateOfBirth':
      const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
      return dateRegex.test(sanitized);
    case 'firstName':
    case 'lastName':
      // Allow letters, hyphens, apostrophes, periods for names (no spaces)
      // Check for dangerous patterns instead of restrictive allowed characters
      const nameDangerousPatterns = /[<>]|javascript:|vbscript:|on\w+\s*=/i;
      return !nameDangerousPatterns.test(sanitized) && sanitized.length >= 1;
    case 'emergencyContact':
    case 'parentName':
    case 'shortText':
      // Allow letters, spaces, hyphens, apostrophes, periods for other names
      // Check for dangerous patterns instead of restrictive allowed characters
      const otherNameDangerousPatterns = /[<>]|javascript:|vbscript:|on\w+\s*=/i;
      return !otherNameDangerousPatterns.test(sanitized) && sanitized.length >= 1;
    case 'address':
    case 'allergies':
    case 'medicalInfo':
      // Allow most characters for addresses and medical info, just exclude dangerous ones
      // Check for dangerous patterns instead of restrictive allowed characters
      const dangerousPatterns = /[<>]|javascript:|vbscript:|on\w+\s*=/i;
      return !dangerousPatterns.test(sanitized) && sanitized.length >= 1;
    default:
      return sanitized.length > 0;
  }
};

// Security event logging
const logSecurityEvent = (event: string, details: any) => {
  console.warn(`Security Event: ${event}`, {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    details
  });
};

const formatPhoneNumber = (value: string): string => {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, "");
  // Format as XXX-XXX-XXXX
  if (digits.length <= 3) {
    return digits;
  } else if (digits.length <= 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  } else if (digits.length <= 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  } else {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
};

// Format currency for display (e.g., 12.34 -> "$12.34")
const formatCurrencyDisplay = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// Initialize Stripe
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
const stripePromise = loadStripe(stripePublishableKey);

// Replace the PaymentForm with a simple redirect flow
function PaymentForm({
  amount,
  eventType,
  eventId,
  customerEmail,
  registrationData,
  onPaymentSuccess,
  onPaymentError,
}: {
  amount: number;
  eventType: string;
  eventId: string;
  customerEmail: string;
  registrationData: RegistrationData;
  onPaymentSuccess: (paymentIntentId: string) => void;
  onPaymentError: (error: string) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRedirect = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stripe/event-registration-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_id: eventId,
          amount,
          event_type: eventType,
          customer_email: customerEmail,
          registration_data: registrationData, // Send the complete form data
        }),
      });
      
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || 'Failed to start payment.');
        setLoading(false);
      }
    } catch (err) {
      setError('Failed to start payment.');
      setLoading(false);
    }
  };

  useEffect(() => {
    // Automatically redirect when the payment form is shown
    handleRedirect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col items-center justify-center min-h-[200px]">
      {loading && <div className="text-indigo-600 text-lg font-semibold">Redirecting to payment...</div>}
      {error && <div className="text-red-600 text-sm mt-2">{error}</div>}
    </div>
  );
}



export default function PublicEventRegistration() {
  const { eventId } = useParams<{ eventId: string }>();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<RegistrationData>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dateOfBirth: "",
    address: "",
    emergencyContact: "",
    allergies: "",
    medicalInfo: "",
    experienceLevel: "",
    beltRank: "",
    beltSize: "",
    uniformSize: "",
    tShirtSize: "",
    howDidYouHear: "",
    parentName: "",
    parentPhone: "",
    parentEmail: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "general",
  });
  const closeNotification = () =>
    setNotification({ ...notification, show: false });
  const [showThankYou, setShowThankYou] = useState(false);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [registrationSubmitted, setRegistrationSubmitted] = useState(false);
  
  // Security-related state
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);
  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  useEffect(() => {
    if (eventId) {
      fetchEvent();
      fetchCsrfToken();
    }
  }, [eventId]);

  const fetchCsrfToken = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/csrf-token`);
      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          setCsrfToken(data.token);
        } else {
          logSecurityEvent('CSRF_TOKEN_INVALID_RESPONSE', { data });
        }
      } else {
        logSecurityEvent('CSRF_TOKEN_FETCH_ERROR', { status: response.status, statusText: response.statusText });
      }
    } catch (error) {
      logSecurityEvent('CSRF_TOKEN_FETCH_FAILED', { error: error instanceof Error ? error.message : 'Unknown error' });
      // In development, generate a fallback token
      if (process.env.NODE_ENV !== 'production') {
        const fallbackToken = Math.random().toString(36).substring(2) + Date.now().toString(36);
        setCsrfToken(fallbackToken);
        console.warn('Using fallback CSRF token in development mode');
      }
    }
  };

  useEffect(() => {
    if (event) {
      // Always start with the registration form, even if payment is required
      // Payment form will be shown after user submits the registration form
    }
  }, [event]);

  const fetchEvent = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/events/public/${eventId}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setEvent(data);
      } else {
        const errorData = await response.json();
        setNotification({
          show: true,
          message: errorData.error || "Event not found or registration is not available",
          type: "error",
        });
      }
    } catch (error) {
      setNotification({
        show: true,
        message: "Error loading event",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof RegistrationData, value: string | boolean) => {
    let newValue = value;

    // Sanitize string inputs
    if (typeof value === 'string') {
      newValue = sanitizeInput(value, field);
      
      // Remove spaces from name fields
      if (field === "firstName" || field === "lastName") {
        newValue = newValue.replace(/\s/g, '');
      }
      
      // Validate input length
      if (MAX_LENGTHS[field] && newValue.length > MAX_LENGTHS[field]) {
        newValue = newValue.substring(0, MAX_LENGTHS[field]);
      }
    }

    // Apply phone number formatting
    if (field === "phone" || field === "parentPhone") {
      newValue = formatPhoneNumber(newValue as string);
    }

    // Apply date formatting for dateOfBirth field
    if (field === "dateOfBirth") {
      newValue = formatDateInput(newValue as string);
    }

    // Handle boolean values for checkboxes
    if (field === "termsConditions") {
      newValue = value === "true" || value === true;
    }

    setFormData((prev) => ({
      ...prev,
      [field]: newValue,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Enhanced validation for required fields
    if (!validateInput(formData.firstName, 'firstName')) {
      newErrors.firstName = "Please enter a valid first name";
    }
    if (!validateInput(formData.lastName, 'lastName')) {
      newErrors.lastName = "Please enter a valid last name";
    }
    if (!validateInput(formData.email, 'email')) {
      newErrors.email = "Please enter a valid email address";
    }

    // Check required fields based on event registration fields with enhanced validation
    if (event?.registration_fields?.includes("phone")) {
      if (!formData.phone?.trim()) {
        newErrors.phone = "Phone number is required";
      } else if (!validateInput(formData.phone, 'phone')) {
        newErrors.phone = "Please enter a valid phone number";
      }
    }
    if (event?.registration_fields?.includes("date_of_birth")) {
      if (!formData.dateOfBirth?.trim()) {
        newErrors.dateOfBirth = "Date of birth is required";
      } else if (!validateInput(formData.dateOfBirth, 'dateOfBirth')) {
        newErrors.dateOfBirth = "Please enter a valid date (MM/DD/YYYY)";
      }
    }
    if (event?.registration_fields?.includes("address")) {
      if (!formData.address?.trim()) {
        newErrors.address = "Address is required";
      } else if (!validateInput(formData.address, 'address')) {
        newErrors.address = "Please enter a valid address";
      }
    }
    if (event?.registration_fields?.includes("emergency_contact")) {
      if (!formData.emergencyContact?.trim()) {
        newErrors.emergencyContact = "Emergency contact is required";
      } else if (!validateInput(formData.emergencyContact, 'emergencyContact')) {
        newErrors.emergencyContact = "Please enter a valid emergency contact name";
      }
    }
    if (event?.registration_fields?.includes("allergies")) {
      if (!formData.allergies?.trim()) {
        newErrors.allergies = "Allergies/medical information is required";
      } else if (!validateInput(formData.allergies, 'allergies')) {
        newErrors.allergies = "Please enter valid allergies or medical information";
      }
    }
    if (event?.registration_fields?.includes("medical_info")) {
      if (!formData.medicalInfo?.trim()) {
        newErrors.medicalInfo = "Medical information is required";
      } else if (!validateInput(formData.medicalInfo, 'medicalInfo')) {
        newErrors.medicalInfo = "Please enter valid medical information";
      }
    }
    SHORT_TEXT_REGISTRATION_FIELD_IDS.forEach((fieldId) => {
      if (!event?.registration_fields?.includes(fieldId)) return;

      const formKey = getRegistrationFormDataKey(fieldId);
      if (!formKey) return;

      const value = formData[formKey as keyof RegistrationData] as string | undefined;
      const label = getRegistrationFieldLabel(fieldId);

      if (!value?.trim()) {
        newErrors[formKey] = `${label} is required`;
      } else if (!validateInput(value, "shortText")) {
        newErrors[formKey] = `Please enter a valid ${label.toLowerCase()}`;
      }
    });
    if (event?.registration_fields?.includes("how_did_you_hear")) {
      if (!formData.howDidYouHear?.trim()) {
        newErrors.howDidYouHear = "Please tell us how you heard about us";
      } else if (!HOW_DID_YOU_HEAR_OPTIONS.includes(formData.howDidYouHear)) {
        newErrors.howDidYouHear = "Please select a valid option";
      }
    }
    if (event?.registration_fields?.includes("parent_name")) {
      if (!formData.parentName?.trim()) {
        newErrors.parentName = "Parent/Guardian name is required";
      } else if (!validateInput(formData.parentName, 'parentName')) {
        newErrors.parentName = "Please enter a valid parent/guardian name";
      }
    }
    if (event?.registration_fields?.includes("parent_phone")) {
      if (!formData.parentPhone?.trim()) {
        newErrors.parentPhone = "Parent/Guardian phone is required";
      } else if (!validateInput(formData.parentPhone, 'parentPhone')) {
        newErrors.parentPhone = "Please enter a valid parent/guardian phone number";
      }
    }
    if (event?.registration_fields?.includes("parent_email")) {
      if (!formData.parentEmail?.trim()) {
        newErrors.parentEmail = "Parent/Guardian email is required";
      } else if (!validateInput(formData.parentEmail, 'parentEmail')) {
        newErrors.parentEmail = "Please enter a valid parent/guardian email address";
      }
    }
    if (event?.registration_fields?.includes("terms_conditions") && !formData.termsConditions) {
      newErrors.termsConditions = "You must agree to the terms and conditions to register for this event";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Client-side rate limiting
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        type: "error",
      });
      return;
    }

    // CSRF token validation (strict in production, flexible in development)
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { eventId });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          type: "error",
        });
        return;
      } else {
        // In development, log the issue but allow submission to continue
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    // ReCaptcha validation (always required)
    if (!recaptchaValue && process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
      setNotification({
        show: true,
        message: "Please complete the reCAPTCHA verification",
        type: "error",
      });
      return;
    }

    setLastSubmissionTime(now);

    // If payment is required, store registration data and show payment form
    if (event?.charge_for_event && event?.charge_amount && event.charge_amount > 0) {
      // Store registration data in localStorage for retrieval after payment
      localStorage.setItem(`event_registration_${eventId}`, JSON.stringify(formData));
      
      setRegistrationSubmitted(true);
      setNotification({
        show: true,
        message: "Please complete your payment to finalize your registration.",
        type: "success",
      });
      return;
    }

    // If no payment required, save registration immediately
    const registrationResult = await submitRegistration();
    
    if (!registrationResult.success) {
      return;
    }
  };

  const submitRegistration = async (paymentIntentId?: string) => {
    setSubmitting(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/events/public/${eventId}/register`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            registration_data: formData,
            payment_intent_id: paymentIntentId,
            csrfToken,
            recaptchaToken: recaptchaValue,
          }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        
        // Clear all errors
        setErrors({});
        
        // Reset form to initial state
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          dateOfBirth: "",
          address: "",
          emergencyContact: "",
          allergies: "",
          medicalInfo: "",
          experienceLevel: "",
          beltRank: "",
          beltSize: "",
          uniformSize: "",
          tShirtSize: "",
          howDidYouHear: "",
          parentName: "",
          parentPhone: "",
          parentEmail: "",
        });

        // Show thank you state
        setShowThankYou(true);

        // After 3 seconds, hide the thank you and refresh
        setTimeout(() => {
          setShowThankYou(false);
          // Optionally refresh the event data
          if (eventId) {
            fetchEvent();
          }
        }, 3000);
        
        return { success: true, registrationId: result.registration_id };
      } else {
        if (response.status === 429) {
          throw new Error('Rate limit exceeded. Please try again later.');
        }
        
        // Try to get detailed error message
        try {
          const errorData = await response.json();
          console.error('Server error response:', errorData);
          throw new Error(errorData.error || 'Failed to submit registration');
        } catch (parseError) {
          throw new Error('Failed to submit registration');
        }
      }
    } catch (error) {
      logSecurityEvent('FORM_SUBMISSION_ERROR', { 
        error: error instanceof Error ? error.message : 'Unknown error',
        eventId 
      });
      setNotification({
        show: true,
        message: error instanceof Error ? error.message : "Error submitting registration. Please try again.",
        type: "error",
      });
      return { success: false, message: error instanceof Error ? error.message : "Error submitting registration. Please try again." };
    } finally {
      setSubmitting(false);
      
      // Reset reCAPTCHA after submission attempt
      setRecaptchaValue(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    }
  };

  const handlePaymentSuccess = async (paymentIntentId: string) => {
    setPaymentIntentId(paymentIntentId);
    
    // Save the registration data with the payment intent ID
    try {
      const registrationResult = await submitRegistration(paymentIntentId);
      
      if (registrationResult.success) {
        setNotification({
          show: true,
          message: "Payment completed successfully! Your registration is now complete.",
          type: "success",
        });
      } else {
        setNotification({
          show: true,
          message: "Payment completed but there was an issue saving your registration. Please contact support.",
          type: "warning",
        });
      }
    } catch (error) {
      setNotification({
        show: true,
        message: "Payment completed but there was an issue saving your registration. Please contact support.",
        type: "warning",
      });
    }
  };

  const handlePaymentError = (error: string) => {
    setNotification({
      show: true,
      message: error,
      type: "error",
    });
  };

  // Map field types to RegistrationData property names
  const getFieldProperty = (fieldType: string): keyof RegistrationData => {
    const formKey = getRegistrationFormDataKey(fieldType);
    return (formKey as keyof RegistrationData) || (fieldType as keyof RegistrationData);
  };

  const renderField = (fieldType: string, label: string, required: boolean = false) => {
    const IconComponent = FIELD_ICONS[fieldType as keyof typeof FIELD_ICONS];
    const fieldProperty = getFieldProperty(fieldType);

    switch (fieldType) {
      case "first_name":
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-1">
              {label} {required && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
              {IconComponent && (
                <IconComponent className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              )}
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange("firstName", e.target.value)}
                className={`w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${getInputClassName("")} ${errors.firstName ? "border-red-500" : ""}`}
                placeholder={`Enter ${label.toLowerCase()}`}
              />
            </div>
            {errors.firstName && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.firstName}</p>
            )}
          </div>
        );

      case "last_name":
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-1">
              {label} {required && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
              {IconComponent && (
                <IconComponent className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              )}
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange("lastName", e.target.value)}
                className={`w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${getInputClassName("")} ${errors.lastName ? "border-red-500" : ""}`}
                placeholder={`Enter ${label.toLowerCase()}`}
              />
            </div>
            {errors.lastName && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.lastName}</p>
            )}
          </div>
        );

      case "email":
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-1">
              {label} {required && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
              {IconComponent && (
                <IconComponent className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              )}
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                className={`w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${getInputClassName("")} ${errors.email ? "border-red-500" : ""}`}
                placeholder="Enter email address"
              />
            </div>
            {errors.email && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email}</p>
            )}
          </div>
        );

      case "phone":
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-1">
              {label} {required && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
              {IconComponent && (
                <IconComponent className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              )}
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange("phone", e.target.value)}
                className={`w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${getInputClassName("")} ${errors.phone ? "border-red-500" : ""}`}
                placeholder="Enter phone number"
              />
            </div>
            {errors.phone && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.phone}</p>
            )}
          </div>
        );

      case "date_of_birth":
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-1">
              {label} {required && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
              {IconComponent && (
                <IconComponent className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              )}
              <input
                type="text"
                value={formData.dateOfBirth}
                onChange={(e) => {
                  const formattedValue = formatDateInput(e.target.value);
                  handleInputChange("dateOfBirth", formattedValue);
                }}
                className={`w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${getInputClassName("")} ${errors.dateOfBirth ? "border-red-500" : ""}`}
                placeholder="MM/DD/YYYY"
              />
            </div>
            {errors.dateOfBirth && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.dateOfBirth}</p>
            )}
          </div>
        );

      case "address":
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-1">
              {label} {required && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
              {IconComponent && (
                <IconComponent className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              )}
              <textarea
                rows={3}
                value={formData.address}
                onChange={(e) => handleInputChange("address", e.target.value)}
                className={`w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${getInputClassName("")} ${errors.address ? "border-red-500" : ""}`}
                placeholder="Enter your address"
              />
            </div>
            {errors.address && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.address}</p>
            )}
          </div>
        );

      case "terms_conditions":
        return (
          <div className="mb-4">
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                id="terms_conditions"
                checked={formData.termsConditions || false}
                onChange={(e) => handleInputChange("termsConditions", e.target.checked.toString())}
                className="h-4 w-4 mt-1 text-indigo-600 dark:text-indigo-400 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700"
              />
              <div className="flex-1">
                <label htmlFor="terms_conditions" className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-1">
                  {label} {required && <span className="text-red-500">*</span>}
                  {required && <span className="text-xs text-red-500 ml-1">(Required)</span>}
                </label>
                <p className="text-xs text-gray-600 dark:text-gray-300 font-medium">
                  {event?.custom_terms_conditions?.trim() || 
                   "I agree to the terms and conditions for this event. By checking this box, I acknowledge that I have read and agree to comply with all event policies, safety guidelines, and studio rules."}
                </p>
                {errors.termsConditions && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.termsConditions}</p>
                )}
              </div>
            </div>
          </div>
        );

      case "how_did_you_hear": {
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-1">
              {label} {required && <span className="text-red-500">*</span>}
            </label>
            <select
              value={(formData[fieldProperty] as string) || ""}
              onChange={(e) => handleInputChange(fieldProperty, e.target.value)}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${getInputClassName("")} ${errors[fieldProperty] ? "border-red-500" : ""}`}
            >
              <option value="">Select {label.toLowerCase()}</option>
              {HOW_DID_YOU_HEAR_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errors[fieldProperty] && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors[fieldProperty]}</p>
            )}
          </div>
        );
      }

      default:
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-100 mb-1">
              {label} {required && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
              {IconComponent && (
                <IconComponent className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              )}
              <input
                type="text"
                value={formData[fieldProperty] as string}
                onChange={(e) => {
                  let newValue = e.target.value;
                  if (fieldType === "phone" || fieldType === "parent_phone") {
                    newValue = formatPhoneNumber(e.target.value);
                  }
                  handleInputChange(fieldProperty, newValue);
                }}
                className={`w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${getInputClassName("")} ${errors[fieldProperty] ? "border-red-500" : ""}`}
                placeholder={`Enter ${label.toLowerCase()}`}
              />
            </div>
            {errors[fieldProperty] && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors[fieldProperty]}</p>
            )}
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading event registration...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Event Not Found</h1>
          <p className="text-gray-600 dark:text-gray-400">This event registration page is not available.</p>
        </div>
      </div>
    );
  }

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(":");
    const date = new Date();
    date.setHours(parseInt(hours, 10));
    date.setMinutes(parseInt(minutes, 10));
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDate = (dateStr: string) => {
    // Handle both ISO date strings and YYYY-MM-DD format consistently
    let date: Date;
    if (dateStr.includes('T')) {
      // ISO date string like "2025-07-18T00:00:00+00:00"
      date = new Date(dateStr);
    } else {
      // YYYY-MM-DD format - create date at midnight in local timezone
      const [year, month, day] = dateStr.split('-').map(Number);
      date = new Date(year, month - 1, day, 0, 0, 0, 0);
    }
    
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <Elements stripe={stripePromise}>
      <style>
        {`
          :root {
            --stripe-text-color: #424770;
            --stripe-placeholder-color: #6b7280;
            --stripe-background-color: #ffffff;
            --stripe-border-color: #d1d5db;
            --stripe-focus-border-color: #3b82f6;
            --stripe-focus-ring-color: rgba(59, 130, 246, 0.5);
          }
          
          .dark {
            --stripe-text-color: #f9fafb;
            --stripe-placeholder-color: #6b7280;
            --stripe-background-color: #1f2937;
            --stripe-border-color: #4b5563;
            --stripe-focus-border-color: #60a5fa;
            --stripe-focus-ring-color: rgba(96, 165, 250, 0.5);
          }
          
          /* Ensure Stripe Elements inherit dark mode styles */
          .dark .StripeElement {
            background-color: var(--stripe-background-color);
            border-color: var(--stripe-border-color);
          }
          
          .dark .StripeElement--focus {
            border-color: var(--stripe-focus-border-color);
            box-shadow: 0 0 0 3px var(--stripe-focus-ring-color);
          }
          
          .dark .StripeElement--invalid {
            border-color: #ef4444;
          }
          
          /* Improve contrast for dark mode */
          .dark .StripeElement {
            color: var(--stripe-text-color) !important;
          }
          
          .dark .StripeElement input {
            color: var(--stripe-text-color) !important;
            background-color: var(--stripe-background-color) !important;
          }
          
          .dark .StripeElement input::placeholder {
            color: var(--stripe-placeholder-color) !important;
          }
        `}
      </style>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
              {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-6xl mx-auto px-4 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {event?.studio?.name ? `${event.studio.name} - Event Registration` : 'Event Registration'}
                </h1>
              </div>
            </div>
          </div>
        </div>

              {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Notification
          show={notification.show}
          message={notification.message}
          onClose={closeNotification}
          type={notification.type as any}
        />

          {showThankYou ? (
            <div className="text-center py-12">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 max-w-md mx-auto">
                <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Registration Complete!
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  Thank you for registering for this event. Your registration has been submitted successfully.
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Event Details */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              {event.registration_image && (
                <div className="mb-6">
                  <img
                    src={event.registration_image}
                    alt="Event"
                    className="w-full max-h-96 object-contain rounded-lg"
                  />
                </div>
              )}

              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                {event.registration_title || event.event_name}
              </h2>

              {event.registration_body && (
                <div className="mb-6">
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {event.registration_body}
                  </p>
                </div>
              )}

              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <CalendarIcon className="h-5 w-5 text-gray-400" />
                  <span className="text-gray-700 dark:text-gray-300">
                    {formatDate(event.from_date)}
                    {event.from_date !== event.end_date && (
                      <>
                        <span className="text-gray-400"> - </span>
                        {formatDate(event.end_date)}
                      </>
                    )}
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <ClockIcon className="h-5 w-5 text-gray-400" />
                  <span className="text-gray-700 dark:text-gray-300">
                    {formatTime(event.start_time)} - {formatTime(event.end_time)}
                  </span>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">
                    {event.show_studio_address_on_registration && event.studio_address
                      ? event.studio_address
                      : `${event.type} Event`}
                  </span>
                </div>
                {event.charge_for_event && (
                  <div className="flex items-center space-x-3">
                    <span className="text-lg font-semibold text-green-600 dark:text-green-400">
                      {formatCurrencyDisplay(event.charge_amount || 0)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Registration Form or Payment Form */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              {!registrationSubmitted ? (
                <>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                    Register for Event
                  </h3>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Render all fields based on event configuration */}
                    {event.registration_fields?.map((fieldType, index) => {
                      const label = getRegistrationFieldLabel(fieldType);
                      const required = ["first_name", "last_name", "email", "phone"].includes(fieldType);

                      return (
                        <div key={`${fieldType}-${index}`}>
                          {renderField(fieldType, label, required)}
                        </div>
                      );
                    })}

                    {/* ReCaptcha (always enabled) */}
                    {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
                      <div className="pt-4">
                        <div className="flex justify-center">
                          <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                            onChange={setRecaptchaValue}
                            onExpired={() => setRecaptchaValue(null)}
                            theme="light"
                          />
                        </div>
                      </div>
                    )}

                    <div className="pt-6">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {submitting ? (
                          <>
                            <ArrowPathIcon className="animate-spin -ml-1 mr-3 h-5 w-5" />
                            Submitting...
                          </>
                        ) : (
                          "Register for Event"
                        )}
                      </button>
                    </div>
                  </form>
                </>
              ) : event.charge_for_event && event.charge_amount && event.charge_amount > 0 ? (
                <>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                    Complete Payment
                  </h3>
                  <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-md">
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      Your registration has been submitted successfully. Please complete your payment to finalize your registration for this event.
                    </p>
                  </div>
                               <PaymentForm
               amount={parseFloat(event.charge_amount?.toString() || '0')}
               eventType={event.type}
               eventId={event.id}
               customerEmail={formData.email}
               registrationData={formData}
               onPaymentSuccess={handlePaymentSuccess}
               onPaymentError={handlePaymentError}
             />
                </>
              ) : (
                <>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                    Registration Complete
                  </h3>
                  <div className="text-center">
                    <p className="text-green-600 dark:text-green-400 mb-4">
                      Your registration has been submitted successfully!
                    </p>
                    <button
                      onClick={() => setRegistrationSubmitted(false)}
                      className="text-indigo-600 hover:text-indigo-500 underline"
                    >
                      Register Another Person
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
          )}
        </div>
      </div>
    </Elements>
  );
} 